import { lazy, Suspense, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Routes, Route, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import LoginPage from './pages/LoginPage';
import { reportSessionStart } from './api/loginAudit';
import { useAuthStore } from './stores/authStore';
import ErrorBoundary from './components/ErrorBoundary';
import ModalRoot from './components/ModalRoot';
import ZoomIndicator from './components/common/ZoomIndicator';   // 🔎 배율 잠깐 표시 (2026-10-13)
import GlobalCallDocks from './components/call/GlobalCallDocks';
import MeetRecHost from './components/meetrec/MeetRecHost';
import FormPopupHost from './components/forms/FormPopupHost';   // 📋 설문 팝업 — TASK 카드·홈에서 /forms?id= (2026-10-07)
import UpdaterBanner from './components/UpdaterBanner';
import SurveyBanner from './components/survey/SurveyBanner';   // 📝 사용자 설문 상단 배너 (2026-09-11)
import FormsBanner from './components/forms/FormsBanner';   // 📋 응답할 설문 상단 배너 (2026-10-08)
import DownloadProgress from './components/DownloadProgress';
import BetaBadge from './components/BetaBadge';   // 🧪 베타 채널 표시 (2026-09-28) — 운영에선 렌더 0
import { useDevGuardOneLine } from './hooks/useDevGuardOneLine';
import { getMembership, getCapability } from './api/membership';
import { fetchProjectKeys } from './api/projects';   // 🔑 채팅 본문 'MT-12' 자동 링크 사전 (2026-09-06 2차-B)
import { isTauri } from './desktop/env';
import { MAIN_NAV_EVENT, openProjectsAnywhere, openCalendarWindow, isPopoutWindow, type MainNavTarget } from './desktop/popout';   // 🧭 팝아웃 → 메인 창 이동 신호 (2026-09-14, 2026-09-19 대상 지정)
import { pushInternalPath } from './utils/openExternal';
import { ensureAutostartDefault } from './desktop/autostart';
import { bumpAppScale, resetAppScale, flashZoom } from './api/uiScale';

// ChatPage 는 tiptap/yjs 정적 import 의존성이 무거움 — 로그인 후에만 다운로드.
const ChatPage = lazy(() => import('./pages/ChatPage'));
// 드라이브 — 데스크톱 스펙 3단 디자인(2026-07-23). 구 DrivePage 는 보존(롤백 대비).
const DrivePage = lazy(() => import('./pages/DriveDesk'));
const DownloadPage = lazy(() => import('./pages/DownloadPage'));
// 🗂 프로젝트 협업 (P1, 2026-08-29) — 대화방과 별도 화면. docs/PJT-협업-설계-20260829.md
const ProjectsPage = lazy(() => import('./pages/ProjectsPage'));
const FeedsPage = lazy(() => import('./pages/FeedsPage'));   // 📡 구독 — 서비스 연동과 별도 메뉴 (2026-09-10)
const ProjectSettingsPage = lazy(() => import('./pages/ProjectSettingsPage'));   // 🗂 프로젝트 설정 별도 창 (#5)
const PjtComparePage = lazy(() => import('./pages/PjtComparePage'));   // ⚖ 카드 2장 비교 (2026-08-31) — 별도 창/모바일 인앱
const SignupPage = lazy(() => import('./pages/SignupPage'));
const InvitePage = lazy(() => import('./pages/InvitePage'));
const ConnectPopoutPage = lazy(() => import('./pages/ConnectPopoutPage'));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'));
// 📅 캘린더 팝아웃 창 (2026-07-10) — 대화와 일정을 나란히 (데탑 별도 창 / 웹 새 창)
const CalendarWorkspace = lazy(() => import('./components/calendar'));
// 🤖 AI 워크스페이스 팝아웃 창 (2026-07-10) — 캘린더와 동일 패턴
const AiPopoutPage = lazy(() => import('./pages/AiPopoutPage'));
// 🛠 관리자 팝아웃 창 (2026-08-05) — 관리자 메뉴를 별도 창으로
const AdminPopoutPage = lazy(() => import('./pages/AdminPopoutPage'));
const VocPopoutPage = lazy(() => import('./pages/VocPopoutPage'));
const JandiPopoutPage = lazy(() => import('./pages/JandiPopoutPage'));
// 🔗 링크 모음 별도 창 (2026-08-31 원격) — 데탑·웹 기본. 모바일·APK 는 인앱 모달(LinksModal) 유지.
const LinksPopoutPage = lazy(() => import('./pages/LinksPopoutPage'));
const WidgetPage = lazy(() => import('./pages/WidgetPage'));   // 🧩 위젯 미니창 (2026-09-19)
// 💡 제안 전체 열람 페이지 (2026-08-25) — 관리자·열람 권한자만(서버 403). 관련부서에 링크로 공유하는 용도.
const ProposalsPage = lazy(() => import('./pages/ProposalsPage'));
const SmsPage = lazy(() => import('./pages/SmsPage'));
const InterpretPage = lazy(() => import('./pages/InterpretPage'));   // 🗣 대면 통역 (2026-10-13, 안드 앱 전용)
const SignupManagePage = lazy(() => import('./pages/SignupManagePage'));
const OrgManagePage = lazy(() => import('./pages/OrgManagePage'));   // 🏢 조직도·인원 관리 (2026-10-07)
const FormsPage = lazy(() => import('./pages/FormsPage'));   // 📋 설문조사(누구나·관리자 승인) (2026-10-07)
// 📖 도움말 센터 (2026-08-16) — 데스크톱은 별도 창, 모바일·앱은 같은 창. 콘텐츠는 언어별로 다시 lazy 분할된다.
const HelpPage = lazy(() => import('./pages/HelpPage'));
// 🖼 파일/사진 미리보기 팝아웃 (2026-08-18) — /preview?k=<인계키>
const PreviewPopoutPage = lazy(() => import('./pages/PreviewPopoutPage'));
// 🖥 화면 공유 시청 팝아웃 (2026-08-18) — /share?sid=&host=
const SharePopoutPage = lazy(() => import('./pages/SharePopoutPage'));

// 💬 대화방 단일 창(solo) — /room?id=<roomId>[&msg=<msgId>]. 팝아웃 창에서 사이드바·네비 없이 그 방만 (2026-07-15).
//   msg — 열리면서 해당 메시지로 점프 (AI 인용 '원문으로 이동', 2026-08-05).
function RoomSoloRoute() {
	const [sp] = useSearchParams();
	const id = sp.get('id') || '';
	const msg = sp.get('msg') || '';
	return <ChatPage soloRoomId={id} soloJumpMessageId={msg || undefined} />;
}

function RequireAuth({ children }: { children: JSX.Element }) {
	const token = useAuthStore((s) => s.authToken);
	const location = useLocation();
	// 🔁 로그인 후 원래 경로로 복귀 (2026-08-25 사용자 — /proposals 링크로 들어와 로그인하면 /chat 으로 튀어 "계속 로그인창").
	//   LoginPage.finishLogin 이 state.from(내부 경로만) 으로 되돌린다.
	if (!token) return <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />;
	return children;
}

// 준회원 온보딩 전용 라우트 — 로그인 + (준회원 && 이름 미확정) 일 때만 진입.
// 그 외(정회원·관리자·이미 확정한 준회원)는 채팅으로 보내 무한 redirect 방지.
function OnboardingRoute() {
	const token = useAuthStore((s) => s.authToken);
	const roles = useAuthStore((s) => s.roles);
	const nameConfirmed = useAuthStore((s) => s.nameConfirmed);
	if (!token) return <Navigate to="/login" replace />;
	const m = getMembership(roles, true);
	if (!(m === 'associate' && !nameConfirmed)) return <Navigate to="/chat" replace />;
	return <OnboardingPage />;
}

// 🔒 보안 fix — 사용자 변경 시 ChatPage/DrivePage 의 React state 누수 차단. userId key 로 강제 remount.
// + capability 라우터 단 이중 검증 — 준회원/비활성이 권한 없는 메뉴 직접 URL 접근 시 redirect.
function AuthedRoute({ children, require }: { children: JSX.Element; require?: 'drive' | 'admin' | 'org' }) {
	const userId = useAuthStore((s) => s.userId);
	const roles = useAuthStore((s) => s.roles);
	const nameConfirmed = useAuthStore((s) => s.nameConfirmed);
	const location = useLocation();
	const membership = getMembership(roles, true);
	const cap = getCapability(membership);
	// 준회원이 이름 미입력(온보딩 미완)이면 가입 화면으로 — 다른 모든 화면보다 우선.
	if (membership === 'associate' && !nameConfirmed) return <Navigate to="/onboarding" replace />;
	// 요구 권한 미충족 시 채팅으로 redirect (모든 권한자가 채팅은 가능)
	if (require === 'drive' && !cap.canManageDrive && location.pathname !== '/chat') return <Navigate to="/chat" replace />;
	if (require === 'admin' && !cap.canSeeAdminPanel && location.pathname !== '/chat') return <Navigate to="/chat" replace />;
	if (require === 'org' && !cap.canSeeOrgTree && location.pathname !== '/chat') return <Navigate to="/chat" replace />;
	return (
		<RequireAuth>
			<div key={userId || 'anon'} className="contents">{children}</div>
		</RequireAuth>
	);
}

export default function App() {
	const { t } = useTranslation();
	useDevGuardOneLine();
	// ⏰ 일정 N분 전 알림 — 서버 스케줄러가 '나와의 대화'에 카드 게시 + ChatPage 가 토스트 발사로 일원화 (2026-08-03).
	//   기존 클라 스케줄러(useCalendarReminders)는 앱이 열려 있을 때만 울려 "알람이 안 온다" — 제거(중복 토스트 방지).
	// 로그인 감사 — 저장 토큰으로 앱 실행(자동로그인) 시 접속 플랫폼 기록 (탭/실행당 1회, 비밀번호 로그인 직후는 가드로 중복 방지).
	const authToken = useAuthStore((s) => s.authToken);
	const userId = useAuthStore((s) => s.userId);
	useEffect(() => {
		if (authToken && userId) reportSessionStart(authToken, userId, 'token');
	}, [authToken, userId]);
	// 🔑 업무 키 사전 프리로드 — 로그인 후 1회. 실패해도 무해(자동 링크만 안 걸린다)
	useEffect(() => { if (authToken && userId) void fetchProjectKeys(); }, [authToken, userId]);
	// 🧭 팝아웃(프로젝트 전용 창)에서 보낸 '메인 창을 이 화면으로' 신호 — 2026-09-14 사용자
	//   "프로젝트 새창에서 채팅창이나 알림 등의 메뉴로 이동이 안됨". 보내는 쪽 = desktop/popout.goMainNav.
	//   여기는 **메인 창에서만** 듣는다(팝아웃 자신이 받으면 프로젝트 창이 채팅으로 갈아타 원래 증상으로 돌아간다).
	useEffect(() => {
		if (isPopoutWindow()) return;
		const go = (id: string, target?: MainNavTarget) => {
			// 🎯 이동 대상이 실려 오면 그 자리로 (2026-09-19 위젯 미니창) — 대상이 없으면 종전대로 화면 전환만.
			//   프로젝트·일정은 **전용 창 규약**이라 여기(넓은 메인 창)에서 열어야 한다.
			if (target?.pjt !== undefined) { openProjectsAnywhere(target.pjt); return; }
			if (target?.cal) { void openCalendarWindow(); return; }
			// 🧷 450ms 사이에 versionGuard 새로고침(창 복귀 시)이 끼면 방 예약이 사라졌다 (2026-09-29) — main.tsx 조기 캡처와 같은 저장소에 먼저 적어 둔다.
			if (target?.roomId) { try { sessionStorage.setItem('tpt-pending-open-room', JSON.stringify({ rid: target.roomId, at: Date.now() })); } catch { /* private mode */ } }
			pushInternalPath('/chat');
			// ChatPage 가 마운트된 뒤에 받아야 해서 한 박자 뒤 — 기존 네비 전환과 같은 450ms 규약.
			window.setTimeout(() => {
				try { window.dispatchEvent(new CustomEvent('tpt:switch-nav', { detail: { nav: (id === 'calendar' || id === 'ai') ? undefined : id, open: id } })); } catch { /* */ }
				// 방 지정 — 채널 미로드 시 보류됐다가 로드 후 열리는 기존 경로(main.tsx 의 tpt:open-room 큐)를 그대로 쓴다.
				// 메시지 지정(팝아웃 안의 /chat?rid=&mid= 링크, 2026-09-29) — 방 열기+원문 점프는 tpt:jump-message 한 번으로
				if (target?.roomId && target.messageId) {
					try { sessionStorage.removeItem('tpt-pending-open-room'); } catch { /* */ }
					try { window.dispatchEvent(new CustomEvent('tpt:jump-message', { detail: { roomId: target.roomId, messageId: target.messageId } })); } catch { /* */ }
				} else if (target?.roomId) {
					try { window.dispatchEvent(new CustomEvent('tpt:open-room', { detail: { roomId: target.roomId } })); } catch { /* */ }
				}
			}, 450);
		};
		const onMsg = (e: MessageEvent) => {
			if (e.origin !== window.location.origin) return;   // 🔒 다른 오리진이 보낸 것은 무시
			const d = e.data as ({ type?: string; id?: unknown } & MainNavTarget) | null;
			if (!d || d.type !== MAIN_NAV_EVENT) return;
			go(String(d.id || 'chat'), { roomId: d.roomId, messageId: d.messageId, pjt: d.pjt, cal: d.cal });
		};
		window.addEventListener('message', onMsg);
		let unlisten: (() => void) | null = null;
		if (isTauri()) {
			void import('@tauri-apps/api/event')
				.then(({ listen }) => listen<{ id?: string } & MainNavTarget>(MAIN_NAV_EVENT, (ev) => go(String(ev.payload?.id || 'chat'), { roomId: ev.payload?.roomId, messageId: ev.payload?.messageId, pjt: ev.payload?.pjt, cal: ev.payload?.cal })))
				.then((un) => { unlisten = un; })
				.catch(() => undefined);
		}
		return () => { window.removeEventListener('message', onMsg); if (unlisten) unlisten(); };
	}, []);
	// 🔌 데스크톱 부팅 자동 시작 — 기본 ON (2026-08-07 사용자 요청). 최초 1회만 적용하고
	//   이후엔 설정(설정 > 화면)의 사용자 선택을 따른다. 웹/APK 는 no-op.
	useEffect(() => { void ensureAutostartDefault(); }, []);
	// 🔎 앱 글자 크기 조절 (2026-07-24)
	//  - 데스크톱(Tauri): 웹 확대/축소식 — Ctrl/⌘ + 마우스 휠, 그리고 Ctrl/⌘ + = / − / 0 단축키.
	//    (웹 브라우저는 자체 줌이 있으므로 데스크톱만 가로챈다)
	//  - 안드로이드 앱(APK): 네이티브가 볼륨 +/− 키를 가로채 'tpt-app-scale-bump' 이벤트로 전달.
	useEffect(() => {
		// 📱 APK 볼륨키 → 글자 크기 (네이티브 MainActivity 가 dispatch, 모든 환경에서 수신 무해)
		const onVolBump = (e: Event) => {
			const d = Number((e as CustomEvent).detail);
			if (d > 0) bumpAppScale(1); else if (d < 0) bumpAppScale(-1);
			if (d) flashZoom();   // 🔎 조작 중 배율 잠깐 표시 (ZoomIndicator)
		};
		window.addEventListener('tpt-app-scale-bump', onVolBump);

		let cleanupDesktop: (() => void) | undefined;
		// 데스크톱 앱 + 팝아웃 창(AI/캘린더/방)에서 Ctrl+휠·단축키 활성화.
		//   메인 웹 창은 브라우저 자체 줌을 쓰므로 제외하지만, 팝아웃 창(window.open 또는 /ai·/calendar·/room)은
		//   앱 배율로 조절돼야 한다(브라우저 줌은 창 격리라 부모 배율과 어긋남). (2026-07-25)
		const isPopout = typeof window !== 'undefined'
			&& (window.opener != null || /^\/(ai|calendar|room)(\/|$)/.test(window.location.pathname));
		if (isTauri() || isPopout) {
			const onKey = (e: KeyboardEvent) => {
				if (!(e.ctrlKey || e.metaKey) || e.altKey) return;
				if (e.key === '=' || e.key === '+') { e.preventDefault(); bumpAppScale(1); flashZoom(); }
				else if (e.key === '-' || e.key === '_') { e.preventDefault(); bumpAppScale(-1); flashZoom(); }
				else if (e.key === '0') { e.preventDefault(); resetAppScale(); flashZoom(); }
			};
			let lastWheel = 0;
			const onWheel = (e: WheelEvent) => {
				if (!(e.ctrlKey || e.metaKey)) return;
				e.preventDefault();                 // 브라우저/webview 기본 줌 억제
				const now = Date.now();
				if (now - lastWheel < 90) return;    // 한 번 굴릴 때 여러 이벤트 → 폭주 방지
				lastWheel = now;
				bumpAppScale(e.deltaY < 0 ? 1 : -1); // 위로 굴리면 확대
				flashZoom();
			};
			window.addEventListener('keydown', onKey);
			window.addEventListener('wheel', onWheel, { passive: false });
			cleanupDesktop = () => {
				window.removeEventListener('keydown', onKey);
				window.removeEventListener('wheel', onWheel);
			};
		}
		return () => {
			window.removeEventListener('tpt-app-scale-bump', onVolBump);
			cleanupDesktop?.();
		};
	}, []);
	return (
		<ErrorBoundary>
			<ModalRoot />
			<ZoomIndicator />
			{/* 📞 보이스톡·방 화면공유 도크 — 라우트와 무관하게 1회 (2026-09-04, 종전 ChatPage 안 → 다른 화면에선 벨이 안 울렸다) */}
			<GlobalCallDocks />
			{/* 🎙 회의 녹음 — 시작 창·녹음 바·회의록 창 (2026-10-26, 라우트 무관 1회) */}
			<MeetRecHost />			<FormPopupHost />
			<UpdaterBanner />
			<SurveyBanner />
			<FormsBanner />
			<DownloadProgress />
			<BetaBadge />
			<Suspense fallback={<div className="flex min-h-screen items-center justify-center text-ui-sm text-fg-subtle">{t('shell.loadingPage')}</div>}>
				<Routes>
					<Route path="/login" element={<LoginPage />} />
					{/* 앱 다운로드 안내 — 공개 (로그인 전에도 접근 가능) */}
					<Route path="/download" element={<DownloadPage />} />
					{/* 👥 직원 가입신청 — 공개, 웹 전용 (앱 셸에선 페이지가 자체 안내로 차단) */}
					<Route path="/signup" element={<SignupPage />} />
					{/* 🎟 초대 링크 활성화 — 공개(토큰이 자격증명). 준회원이 비밀번호를 직접 설정 */}
					<Route path="/invite" element={<InvitePage />} />
					{/* 준회원 가입(이름 입력) — 로그인했지만 이름 미확정 준회원 전용 */}
					<Route path="/onboarding" element={<OnboardingRoute />} />
					<Route
						path="/chat/*"
						element={
							<AuthedRoute>
								<ChatPage />
							</AuthedRoute>
						}
					/>
					<Route
						path="/room"
						element={
							<AuthedRoute>
								<RoomSoloRoute />
							</AuthedRoute>
						}
					/>
					<Route
						path="/calendar"
						element={
							<AuthedRoute>
								<div className="h-[100dvh] w-screen overflow-hidden"><CalendarWorkspace /></div>
							</AuthedRoute>
						}
					/>
					<Route
						path="/ai"
						element={
							<AuthedRoute>
								<AiPopoutPage />
							</AuthedRoute>
						}
					/>
					<Route
						path="/admin"
						element={
							<AuthedRoute>
								<AdminPopoutPage />
							</AuthedRoute>
						}
					/>
					{/* 📮 VOC(문의·개선 요청) 별도 창 (2026-08-24) — 입력 공간을 창 크기만큼 */}
					<Route
						path="/voc"
						element={
							<AuthedRoute>
								<VocPopoutPage />
							</AuthedRoute>
						}
					/>
					{/* 🧩 위젯 미니창 (2026-09-19) — 데스크톱 트레이 메뉴에서 진입. 항상 위·작은 창 */}
					<Route
						path="/widget"
						element={
							<AuthedRoute>
								<WidgetPage />
							</AuthedRoute>
						}
					/>
					{/* 🔗 링크 모음 별도 창 (2026-08-31) — 홈/방 헤더 링크 아이콘에서 진입 */}
					<Route
						path="/links"
						element={
							<AuthedRoute>
								<LinksPopoutPage />
							</AuthedRoute>
						}
					/>
					{/* 🗂 잔디 이력 별도 창 (2026-08-29) — 방 헤더 ⋯ 에서 진입. /jandi?rid=&name= */}
					<Route
						path="/jandi"
						element={
							<AuthedRoute>
								<JandiPopoutPage />
							</AuthedRoute>
						}
					/>
					{/* 📨 경조사 문자 발송 (2026-08-26) — 발송 권한자만(서버 판정). 권한 없으면 안내만 보인다. */}
					<Route
						path="/sms"
						element={
							<AuthedRoute>
								<SmsPage />
							</AuthedRoute>
						}
					/>
					{/* 🗣 대면 통역 (2026-10-13) — 안드 앱 더보기에서 진입. 앱이 아니거나 관리자가 아니면 페이지가 안내만 보인다. */}
					<Route
						path="/interpret"
						element={
							<AuthedRoute>
								<InterpretPage />
							</AuthedRoute>
						}
					/>
					{/* 🧭 직접가입 인원 관리 (2026-08-28) — 위임 담당자·관리자만(서버 판정). 권한 없으면 안내만 보인다. */}
					<Route
						path="/signup-manage"
						element={
							<AuthedRoute>
								<SignupManagePage />
							</AuthedRoute>
						}
					/>
					{/* 📋 설문조사 (2026-10-07) — 정회원 누구나 만들고 관리자 승인 후 공개 */}
					<Route
						path="/forms"
						element={
							<AuthedRoute>
								<FormsPage />
							</AuthedRoute>
						}
					/>
					{/* 🏢 조직도·인원 관리 (2026-10-07) — 관리자·지정 담당자(인사팀)만(서버 판정). 권한 없으면 안내만 보인다. */}
					<Route
						path="/org-manage"
						element={
							<AuthedRoute>
								<OrgManagePage />
							</AuthedRoute>
						}
					/>
					<Route
						path="/proposals"
						element={
							<AuthedRoute>
								<ProposalsPage />
							</AuthedRoute>
						}
					/>
					{/* 🖼 파일/사진 미리보기 별도 창 (2026-08-18) — 사진을 보면서 대화하기 위한 분리 창 */}
					<Route
						path="/preview"
						element={
							<AuthedRoute>
								<PreviewPopoutPage />
							</AuthedRoute>
						}
					/>
					{/* 🖥 화면 공유 시청 별도 창 (2026-08-18) — 대화창을 가리지 않고 발표 화면만 */}
					<Route
						path="/share"
						element={
							<AuthedRoute>
								<SharePopoutPage />
							</AuthedRoute>
						}
					/>
					{/* 🔌 서비스 연동 별도 창 (2026-08-13) */}
					<Route
						path="/connect"
						element={
							<AuthedRoute>
								<ConnectPopoutPage />
							</AuthedRoute>
						}
					/>
					{/* 🗂 프로젝트 협업 — 상단 네비 '대화' 옆 버튼(2026-08-29 사용자). 준회원 차단은 서버(requireNonGuest)가 한다. */}
					<Route
						path="/projects"
						element={
							<AuthedRoute>
								<ProjectsPage />
							</AuthedRoute>
						}
					/>
					{/* 📡 구독(Feeds) — 사이드바 외부 연동 섹션의 '구독' 항목. 준회원 차단은 서버(requireNonGuest). (2026-09-10) */}
					<Route
						path="/feeds"
						element={
							<AuthedRoute>
								<FeedsPage />
							</AuthedRoute>
						}
					/>
					<Route
						path="/pjt-settings"
						element={
							<AuthedRoute>
								<ProjectSettingsPage />
							</AuthedRoute>
						}
					/>
					<Route
						path="/pjt-compare"
						element={
							<AuthedRoute>
								<PjtComparePage />
							</AuthedRoute>
						}
					/>
					<Route
						path="/drive"
						element={
							<AuthedRoute require="drive">
								<DrivePage />
							</AuthedRoute>
						}
					/>
					{/* 📖 도움말 — 권한 게이트 없음(전원 공개). 관리자면 관리자 전용 문서까지 보인다. */}
					<Route
						path="/help"
						element={
							<AuthedRoute>
								<HelpPage />
							</AuthedRoute>
						}
					/>
					<Route path="*" element={<Navigate to="/chat" replace />} />
				</Routes>
			</Suspense>
		</ErrorBoundary>
	);
}
